import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, GraduationCap, Award, Building2, Sparkles } from "lucide-react";
import { useSEO } from "../hooks/useSEO";
import { useJsonLd, personSchema, breadcrumb } from "../hooks/useJsonLd";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-100px" }, transition: { duration: 0.6 } };

const timeline = [
  { year: "2018", title: "Digital Marketing", note: "Started in digital marketing across performance and native." },
  { year: "2020", title: "Native Advertising Specialist", note: "Scaled Nutralyfe and other brands on Taboola / Outbrain." },
  { year: "2022", title: "Growth Marketing Lead", note: "Managed high-profile brands including Kama Ayurveda, Paytm, Satyapal." },
  { year: "2023", title: "OTT Growth Strategist", note: "Built acquisition + retention systems for Styx Sports and Sandbrix." },
  { year: "2024", title: "Founder · Aiadspace", note: "Launched performance marketing agency scaling D2C + marketplace brands." },
  { year: "2025", title: "Co-Founder · Above40", note: "D2C skincare launched on Shopify + Amazon + Flipkart. Shortlisted for Shark Tank India S5." },
  { year: "2026", title: "AI Growth Marketing", note: "Building autonomous marketing agents + IPL/SA20/BBL campaign systems." },
];

const roles = [
  { org: "Aiadspace", role: "Founder", years: "2024 — Present" },
  { org: "Above40", role: "Co-Founder · Branding & Marketing", years: "2025 — Present" },
  { org: "Styx Sports", role: "Project Manager · OTT Growth", years: "2023 — Present" },
];

const certifications = [
  "Leading in Crisis",
  "Affiliate Academy · Voluum",
  "Digital Marketing Certification",
  "AWS DSP · International Performance Marketing",
];

const beliefs = [
  { t: "Systems compound. Campaigns don't.", d: "Every engagement should leave behind a repeatable, documented growth system — not a one-off spike." },
  { t: "AI belongs in the workflow, not the pitch.", d: "The best AI wins aren't demos — they're 30% fewer manual reports and 10× faster budget decisions." },
  { t: "Own the funnel, not just the ad.", d: "Landing, offer, retention and reporting matter more than the platform you're running on." },
  { t: "Numbers before narrative.", d: "Data-first every time. Story frames what the numbers already show." },
];

export default function About() {
  useSEO({
    title: "About Shivani Ranawat · AI Growth Marketing Consultant & Aiadspace Founder",
    description: "Shivani Ranawat is an AI Growth Marketing Consultant with 7+ years scaling 50+ brands. Founder of Aiadspace, co-founder of Above40, growth strategist for IPL, SA20 and BBL campaigns.",
    path: "/about",
  });
  useJsonLd([
    personSchema,
    breadcrumb([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
  ]);
  return (
    <div data-testid="about-page">
      {/* Intro */}
      <section className="relative pt-32 pb-16 md:pb-24 bg-[#0F1115] text-white mesh-bg grain overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 relative">
          <motion.div {...fadeUp}>
            <div className="text-xs font-mono tracking-[0.2em] uppercase text-white/50">About</div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-7xl leading-[1.02] tracking-tight max-w-4xl">
              I don&apos;t run campaigns.<br />
              <span className="text-white/50">I build revenue systems</span> that outlive them.
            </h1>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7 text-white/75 text-base md:text-lg leading-relaxed space-y-4">
                <p>
                  I&apos;m a Performance &amp; Growth Marketing leader with 7+ years driving paid acquisition and full-funnel strategy for 50+ international brands. I manage budgets from $4K to $125K+ per campaign, consistently delivering 5–7x ROAS across Google Ads, Meta Ads, Amazon DSP, and programmatic native (Taboola, Outbrain, MGID).
                </p>
                <p>
                  As founder of <span className="text-white">Aiadspace</span>, I&apos;ve helped D2C and marketplace brands hit up to 200% sales growth through hyper-targeted, data-driven campaigns — combining acquisition strategy with retention systems (email, WhatsApp automation) to maximise LTV, not just first-click ROI.
                </p>
                <p>
                  Recent work spans co-founding a D2C skincare brand (<span className="text-white">Above40</span>, from launch to Shopify + Amazon + Flipkart scale) and leading GTM and user-acquisition strategy for major cricket tournaments — <span className="text-white">IPL, SA20, BBL</span> — where I scale around peak-intent match windows while driving CAC down through advanced retargeting.
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="glass-dark rounded-2xl p-6">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mb-4">At a glance</div>
                  <dl className="space-y-3 text-sm">
                    <div className="flex items-center justify-between border-b border-white/5 pb-2">
                      <dt className="text-white/60 inline-flex items-center gap-2"><MapPin size={12} /> Based</dt><dd>India · Remote-first</dd>
                    </div>
                    <div className="flex items-center justify-between border-b border-white/5 pb-2">
                      <dt className="text-white/60">Experience</dt><dd>7+ Years</dd>
                    </div>
                    <div className="flex items-center justify-between border-b border-white/5 pb-2">
                      <dt className="text-white/60">Brands scaled</dt><dd>50+</dd>
                    </div>
                    <div className="flex items-center justify-between border-b border-white/5 pb-2">
                      <dt className="text-white/60">Avg ROAS</dt><dd className="text-[#10B981]">5 – 7×</dd>
                    </div>
                    <div className="flex items-center justify-between border-b border-white/5 pb-2">
                      <dt className="text-white/60">Budget range</dt><dd>$4K → $125K+ / campaign</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-white/60">Availability</dt><dd className="text-[#10B981]">Q1 2026 · Open</dd>
                    </div>
                  </dl>
                </div>
                <div className="mt-3 flex gap-3">
                  <Link to="/contact" className="flex-1 inline-flex justify-center items-center gap-2 rounded-full bg-white text-neutral-900 px-5 py-3 text-sm font-medium">Book a Strategy Call <ArrowRight size={14} /></Link>
                  <Link to="/work" className="inline-flex items-center gap-2 rounded-full border border-white/20 text-white px-5 py-3 text-sm font-medium">Case Studies</Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Beliefs */}
      <section className="bg-white py-20 md:py-28 border-t border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-3 text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">
              Operating Beliefs
            </div>
            <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-4">
              {beliefs.map((b, i) => (
                <div key={b.t} data-testid={`belief-${i + 1}`} className="rounded-2xl border border-neutral-200 p-6 hover:border-[#3B82F6] transition">
                  <div className="text-[10px] font-mono text-[#3B82F6]">{String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-4 font-display text-xl text-neutral-900">{b.t}</div>
                  <div className="mt-2 text-sm text-neutral-600">{b.d}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline + roles */}
      <section className="bg-[#F7F7F5] py-20 md:py-28 border-t border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="text-xs font-mono tracking-[0.2em] uppercase text-neutral-500">Journey</div>
              <h2 className="mt-4 font-display text-3xl md:text-5xl text-neutral-900 tracking-tight leading-[1.05]">
                Seven years.<br /><span className="text-neutral-400">From native ads to AI agents.</span>
              </h2>

              <div className="mt-10">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-4">Current Roles</div>
                <div className="space-y-3">
                  {roles.map((r) => (
                    <div key={r.org} className="rounded-xl border border-neutral-200 bg-white p-4">
                      <div className="flex items-center gap-2 text-[10px] font-mono text-neutral-400">
                        <Building2 size={12} /> {r.years}
                      </div>
                      <div className="mt-2 font-display text-lg text-neutral-900">{r.org}</div>
                      <div className="text-xs text-neutral-500">{r.role}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="relative pl-6">
                <div className="absolute left-2 top-2 bottom-2 w-px bg-neutral-300" />
                {timeline.map((t) => (
                  <div key={t.year} className="relative mb-5 last:mb-0">
                    <div className="absolute -left-[18px] top-2 w-3 h-3 rounded-full bg-[#3B82F6] ring-4 ring-[#F7F7F5]" />
                    <div className="rounded-xl bg-white border border-neutral-200 p-4">
                      <div className="text-[10px] font-mono text-neutral-400">{t.year}</div>
                      <div className="mt-1 font-display text-lg text-neutral-900">{t.title}</div>
                      <div className="text-sm text-neutral-500">{t.note}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Education + certifications */}
      <section className="bg-white py-20 md:py-28 border-t border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div {...fadeUp} className="rounded-2xl border border-neutral-200 p-8">
              <div className="w-11 h-11 rounded-xl bg-[#3B82F6]/10 text-[#3B82F6] flex items-center justify-center"><GraduationCap size={20} /></div>
              <div className="mt-6 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">Education</div>
              <div className="mt-2 font-display text-2xl text-neutral-900">Maharshi Dayanand University</div>
              <ul className="mt-4 space-y-3 text-sm text-neutral-700">
                <li>MS · Information Technology</li>
                <li>Bachelor of Computer Applications</li>
              </ul>
            </motion.div>

            <motion.div {...fadeUp} className="rounded-2xl border border-neutral-200 p-8">
              <div className="w-11 h-11 rounded-xl bg-[#10B981]/10 text-[#10B981] flex items-center justify-center"><Award size={20} /></div>
              <div className="mt-6 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">Certifications</div>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-neutral-700">
                {certifications.map((c) => (
                  <li key={c} className="inline-flex items-start gap-2"><Sparkles size={12} className="text-[#3B82F6] mt-1" /> {c}</li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
